/**
 * Main entry point for Wyden Expenditure Approval frontend application
 *
 * Handles:
 * - Application initialization
 * - Authentication flow (check auth status on load)
 * - Route registration with auth guards
 * - Loading state during auth check
 * - Browser notifications
 */

import './styles/index.css';
import { createRouter, type RouteContext } from './services/router';
import { setLinkRouter } from './components/link';
import {
  dashboardView,
  kanbanView,
  settingsView,
  adminSettingsView,
  reportsView,
  myRequestsView,
  myApprovalsView,
  allRequestsView,
  travelRequestsView,
  outOfPocketView,
  expenseReportsView,
  expenseReportsDashboardView,
  expenseReportDetailView,
  receiptDetailView,
} from './views/index.js';
import {
  initBrowserNotifications,
  setBrowserNotificationRouter,
  shouldPromptForPermission,
} from './services/browser-notifications';
import { createNotificationPermissionBanner } from './components/notification-permission-banner';
import { getToastContainer } from './components/toast';
import { checkAuthStatus } from './services/auth-service';
import { initSessionManager } from './services/session-manager';
import { requireAuth, requireGuest, requireAdmin, requireRole } from './services/auth-guard';
import { logout as logoutStore } from './services/auth-store';
import { startPageLoading, stopPageLoading } from './services/page-loading';
import { loginView } from './views/login-view';
import { authCallbackView } from './views/auth-callback-view';
import { loadingView } from './views/loading-view';
import { newRequestView } from './views/new-request-view';
import { editRequestView } from './views/edit-request-view';
import { requestDetailView } from './views/request-detail-view';
import { renderIcon } from './utils/icons';
import { createSidebarLayout } from './components/sidebar-layout';

// Get the root element
const app = document.getElementById('app');

if (!app) {
  throw new Error('Root element #app not found');
}

// Create the router instance
const router = createRouter({
  rootElement: app,
  notFoundView: (ctx: RouteContext) => {
    const container = document.createElement('div');
    container.className = 'min-h-screen flex items-center justify-center p-4';
    container.innerHTML = `
      <div class="glass-card w-full max-w-md p-8 rounded-2xl text-center">
        <div class="flex justify-center mb-4">
          <div class="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center">
            ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
          </div>
        </div>
        <h1 class="text-4xl font-bold text-foreground mb-2">404</h1>
        <p class="text-lg text-muted-foreground mb-4">Page not found</p>
        <p class="text-sm text-muted-foreground mb-6">The path "${ctx.path}" does not exist.</p>
        <a href="/" class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors">
          ${renderIcon('home', { size: 'sm' }).outerHTML}
          <span>Go to Home</span>
        </a>
      </div>
    `;
    return container;
  },
});

// Set router for Link components
setLinkRouter(router);

// ============================================================================
// View Definitions
// ============================================================================

/**
 * Home view - redirects to dashboard
 */
const homeView = () => {
  // Redirect to dashboard
  window.history.replaceState({}, '', '/dashboard');
  window.dispatchEvent(new PopStateEvent('popstate'));

  // Return empty container while redirecting
  const container = document.createElement('div');
  container.className = 'min-h-screen flex items-center justify-center';
  container.innerHTML = `<p class="text-muted-foreground">Redirecting...</p>`;
  return container;
};

/**
 * Payment profile view - redirects to settings with payment tab
 */
const paymentProfileRedirectView = () => {
  // Redirect to settings with payment tab
  window.history.replaceState({}, '', '/settings?tab=payment');
  window.dispatchEvent(new PopStateEvent('popstate'));

  // Return empty container while redirecting
  const container = document.createElement('div');
  container.className = 'min-h-screen flex items-center justify-center';
  container.innerHTML = `<p class="text-muted-foreground">Redirecting to payment settings...</p>`;
  return container;
};

/**
 * About view
 */
const aboutView = () => {
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6 max-w-2xl mx-auto';

  const card = document.createElement('div');
  card.className = 'glass-card p-8 rounded-2xl';

  card.innerHTML = `
    <div class="space-y-4 text-muted-foreground">
      <p>
        <strong class="text-foreground">Wyden Expenditure Approval System</strong> is an internal tool for managing
        and approving expense requests within the organization.
      </p>
      <p>Version 1.0.0</p>
      <ul class="list-disc list-inside space-y-2">
        <li>Submit expense requests with file attachments</li>
        <li>Multi-level approval workflow based on amount</li>
        <li>Email and browser notifications</li>
        <li>Delegation support for approvers</li>
      </ul>
    </div>
  `;

  content.appendChild(card);

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/about',
    title: 'About',
    subtitle: 'Wyden Expenditure Approval System',
  });
};

// settingsView is now imported from views/index.js

// ============================================================================
// Route Registration
// ============================================================================

router
  // Auth routes (guest only)
  .register('/login', loginView, [requireGuest()])
  .register('/auth/callback', authCallbackView)
  // Protected routes
  .register('/', homeView, [requireAuth()])
  .register('/dashboard', dashboardView, [requireAuth()])
  .register('/kanban', kanbanView, [requireAdmin()])
  .register('/reports', reportsView, [requireAdmin()])
  .register('/about', aboutView, [requireAuth()])
  .register('/settings', settingsView, [requireAuth()])
  // Redirect /payment-profile to /settings?tab=payment for backwards compatibility
  .register('/payment-profile', paymentProfileRedirectView, [requireAuth()])
  // Request list views
  .register('/my-requests', myRequestsView, [requireAuth()])
  .register('/approvals', myApprovalsView, [requireAuth()])
  // Request routes
  .register('/requests/new', newRequestView, [requireAuth()])
  .register('/requests/:id', requestDetailView, [requireAuth()])
  .register('/requests/:id/edit', editRequestView, [requireAuth()])
  // Expense report routes
  .register('/expense-reports', expenseReportsView, [requireAuth()])
  .register('/expense-reports-dashboard', expenseReportsDashboardView, [requireRole(['ADMIN', 'ASSISTANT'])])
  .register('/expense-reports/:id', expenseReportDetailView, [requireAuth()])
  .register('/expense-reports/:id/receipts/:receiptId', receiptDetailView, [requireAuth()])
  // Assistant role routes (Travel and Out-of-Pocket) - allow both ADMIN and ASSISTANT
  .register('/travel-requests', travelRequestsView, [requireRole(['ADMIN', 'ASSISTANT'])])
  .register('/out-of-pocket', outOfPocketView, [requireRole(['ADMIN', 'ASSISTANT'])])
  // Admin routes (ADMIN role required)
  .register('/admin/settings', adminSettingsView, [requireAdmin()])
  .register('/admin/requests', allRequestsView, [requireAdmin()]);

// ============================================================================
// Application Initialization
// ============================================================================

/**
 * Initialize the application
 */
async function initApp(): Promise<void> {
  console.log('Initializing Wyden Expenditure Approval application...');

  if (!app) {
    throw new Error('Root element #app not found');
  }

  // Initialize toast notification system
  getToastContainer({ position: 'bottom-right', maxToasts: 5 });

  // Show loading view while checking auth
  const loadingElement = loadingView();
  app.innerHTML = '';
  app.appendChild(loadingElement);

  // Check if this is an auth callback (coming back from Azure AD)
  const currentPath = window.location.pathname;
  const isAuthCallback = currentPath === '/auth/callback';

  // Check for session_expired flag - clear any stale auth state immediately
  // This prevents redirect loops by ensuring fresh state before auth check
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('session_expired') === '1') {
    console.log('Session expired flag detected, clearing auth state');
    logoutStore();
  }

  if (!isAuthCallback) {
    // Check authentication status
    try {
      const isAuthenticated = await checkAuthStatus();
      console.log('Auth status checked, authenticated:', isAuthenticated);

      // Initialize session manager for authenticated users
      if (isAuthenticated) {
        initSessionManager({
          authCheckInterval: 5 * 60 * 1000, // Check every 5 minutes
          refreshBeforeExpiry: 2 * 60 * 1000, // Refresh 2 minutes before expiry
          showNotifications: true,
        });
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  }

  // Initialize browser notifications service
  initBrowserNotifications();
  setBrowserNotificationRouter(router);

  // Create notification permission banner (shows automatically if needed)
  if (shouldPromptForPermission()) {
    createNotificationPermissionBanner({
      parent: document.body,
      position: 'top',
      onEnabled: () => {
        console.log('Browser notifications enabled');
      },
      onDismissed: () => {
        console.log('Notification banner dismissed');
      },
    });
  }

  // Add page loading indicator for route transitions
  router.onRouteChange((event) => {
    // Start loading on navigation
    if (event.from !== null && event.from !== event.to) {
      startPageLoading();
    }
  });

  // Stop page loading when view is rendered (handled in renderView)
  // We use a MutationObserver to detect when content is rendered
  const observer = new MutationObserver(() => {
    // Content has changed, stop loading
    stopPageLoading();
  });

  observer.observe(app, { childList: true, subtree: false });

  // Initialize router (this will handle routing based on auth state)
  router.init();

  console.log('Application initialized');
}

// Start the application
initApp().catch(console.error);
